// src/pages/EventRegister.js
// Public event registration page rendered INSIDE the standard site layout
// (Header/Footer via the router's LayoutWithScroll). Mirrors the conventions
// used in ArticleDetail.js / Contact.js (BASE_URL, Helmet, i18n, toastify).
//
// UI/UX pass:
//   * the event poster is shown COMPLETE (object-fit: contain) over an ambient
//     blurred copy of itself — no more cropped artwork or unreadable title,
//   * an orchestrated load sequence (backdrop → poster → text → form),
//   * an animated progress rail for the Entreprise → Participants sequence,
//   * a collapsible brief so the form is reachable immediately,
//   * every animation is disabled under prefers-reduced-motion.
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import eventApi, { BASE_URL } from "../config/eventApi";
import RegCompanyStep from "../components/RegCompanyStep";
import RegParticipantsStep from "../components/RegParticipantsStep";

const emptyParticipant = () => ({
  _key: Math.random().toString(36).slice(2),
  id: null, civilite: "", nom: "", prenom: "", nom_ar: "", prenom_ar: "",
  numero_carte_identite: "", numero_passeport: "", email: "", telephone: "",
  fonction: "", _reused: false,
});

// Shared core so the standalone landing page can reuse the exact same logic.
export function RegistrationCore({ slug, layout = "site" }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  // Registration mode actually used by the visitor: 'company' | 'direct'.
  // For events configured as 'both', null until the visitor chooses.
  const [regMode, setRegMode] = useState(null);

  const [company, setCompany] = useState({ rc: "", raison_sociale: "", email: "" });
  const [otpVerified, setOtpVerified] = useState(false);
  const [participants, setParticipants] = useState([emptyParticipant()]);
  const [payment, setPayment] = useState({ mode_paiement: "", preuve_base64: null, preuve_filename: "" });
  const [submitting, setSubmitting] = useState(false);

  // UI-only state
  const [briefOpen, setBriefOpen] = useState(false);
  const [posterOk, setPosterOk] = useState(true);
  const stageRef = useRef(null);
  const firstRender = useRef(true);

  // When an existing+registered company is found, we manage its registration
  // (add/remove participants, cancel) instead of creating a new one.
  const [existingReg, setExistingReg] = useState(null); // { reference, statut, participants[] }

  // Called by RegCompanyStep when the looked-up company already has a
  // registration for this event.
  const handleExistingRegistration = (registration) => {
    setExistingReg(registration);
    // Seed the participant editor with the existing participants.
    const seeded = (registration.participants || []).map((p) => ({
      _key: Math.random().toString(36).slice(2),
      id: p.id_participant,
      civilite: "",
      nom: p.nom || "",
      prenom: p.prenom || "",
      nom_ar: "",
      prenom_ar: "",
      numero_carte_identite: p.numero_carte_identite || "",
      numero_passeport: p.numero_passeport || "",
      email: p.email || "",
      telephone: p.telephone || "",
      fonction: p.fonction || "",
      _reused: true,
    }));
    setParticipants(seeded.length ? seeded : [emptyParticipant()]);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await eventApi.getEvent(slug, lang);
        if (active) {
          // Send the visitor to the presentation the organiser picked in the
          // admin ("use_layout"), but ONLY when the page they are on is the
          // wrong one. This core is shared by both pages, so comparing
          // against `layout` is what stops it redirecting to itself and
          // leaving a blank screen.
          //   layout="site"       -> /event/{slug}/register  (header, menu, footer)
          //   layout="standalone" -> /e/{slug}               (no site chrome)
          if (data) {
            const wantsLayout = data.use_layout !== false;
            const prefix = lang === "fr" ? "" : `/${lang}`;
            const target = data.slug || slug;

            if (layout === "site" && !wantsLayout) {
              navigate(`${prefix}/e/${target}`, { replace: true });
              return;                       // keep loading: we are leaving
            }
            if (layout === "standalone" && wantsLayout) {
              navigate(`${prefix}/event/${target}/register`, { replace: true });
              return;
            }
          }
          setEvent(data);
          // Pre-resolve mode when the event forces one.
          const m = data.registration_mode || "company";
          if (m !== "both") setRegMode(m);
          // direct mode starts at the participant step directly
          if (m === "direct") setStep(2);
        }
      } catch (e) {
        toast.error(t("reg.event_not_found", "Événement introuvable"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug, lang, t, layout, navigate]);

  // Bring the form into view when the visitor moves to the next step, but
  // never on the very first paint (direct-mode events open on step 2).
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (!stageRef.current) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    stageRef.current.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [step, regMode]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const mode = regMode || "company";

      // Whether this event requires e-mail OTP verification.
      const needOtp = event.require_otp !== false;

      // The OTP-verified email is the company email (company mode) or the
      // single participant's email (direct mode). Omitted when OTP is off.
      const otpEmail = mode === "company" ? company.email : (participants[0]?.email || "");

      const payload = {
        id_event: event.id,
        mode,
        otp_verified_email: needOtp ? otpEmail : null,
        participants: participants.map((p) => ({
          civilite: p.civilite || null,
          nom: p.nom,
          prenom: p.prenom,
          nom_ar: p.nom_ar || null,
          prenom_ar: p.prenom_ar || null,
          numero_carte_identite: p.numero_carte_identite || null,
          numero_passeport: p.numero_passeport || null,
          email: p.email || null,
          telephone: p.telephone || null,
          fonction: p.fonction || null,
        })),
      };

      // Payment (paid events): chosen method + optional proof (base64).
      if (event.is_paid) {
        payload.mode_paiement = payment.mode_paiement || null;
        payload.preuve_base64 = payment.preuve_base64 || null;
        payload.preuve_filename = payment.preuve_filename || null;
      }

      // Company object only in company mode.
      if (mode === "company") payload.company = company;

      const { data } = await eventApi.submit(payload, lang);

      if (data.success) {
        // Paid events now register as PENDING (payment verified by the organizer),
        // so we go straight to the status page instead of a gateway redirect.
        toast.success(data.message || t("reg.submit_ok", "Inscription enregistrée"));
        const prefix = lang === "fr" ? "" : `/${lang}`;
        // Stay in the presentation the organiser chose: a standalone event
        // must not drop the visitor into the full website on confirmation.
        const path = event.use_layout === false
          ? `${prefix}/e/registration/${data.reference}`
          : `${prefix}/event/registration/${data.reference}`;
        navigate(path);
      } else {
        toast.error(data.message || t("reg.submit_failed", "Échec de l'inscription"));
      }
    } catch (e) {
      const msg = e?.response?.data?.message || t("reg.submit_failed", "Échec de l'inscription");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Save changes to an existing registration's participant list.
  const handleUpdateExisting = async () => {
    setSubmitting(true);
    try {
      const payload = {
        reference: existingReg.reference,
        action: "sync",
        otp_verified_email: company.email,
        participants: participants.map((p) => ({
          id_participant: p.id || null,
          civilite: p.civilite || null,
          nom: p.nom,
          prenom: p.prenom,
          numero_carte_identite: p.numero_carte_identite || null,
          numero_passeport: p.numero_passeport || null,
          email: p.email || null,
          telephone: p.telephone || null,
          fonction: p.fonction || null,
        })),
      };
      const { data } = await eventApi.updateRegistration(payload, lang);
      if (data.success) {
        const prefix = lang === "fr" ? "" : `/${lang}`;
        const path = event.use_layout === false
          ? `${prefix}/e/registration/${existingReg.reference}`
          : `${prefix}/event/registration/${existingReg.reference}`;
        navigate(path);
      } else {
        toast.error(data.message || t("reg.update_failed", "Échec de la mise à jour"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || t("reg.update_failed", "Échec de la mise à jour"));
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel an existing registration.
  const handleCancelExisting = async () => {
    if (!window.confirm(t("reg.cancel_confirm", "Annuler définitivement cette inscription ?"))) return;
    setSubmitting(true);
    try {
      const { data } = await eventApi.updateRegistration({
        reference: existingReg.reference,
        action: "cancel",
        otp_verified_email: company.email,
      }, lang);
      if (data.success) {
        toast.success(t("reg.cancelled", "Inscription annulée."));
        setExistingReg(null);
        setParticipants([emptyParticipant()]);
      } else {
        toast.error(data.message || t("reg.cancel_failed", "Échec de l'annulation"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || t("reg.cancel_failed", "Échec de l'annulation"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="evreg">
        <style>{regCss}</style>
        <div className="evreg-boot">
          <span className="evreg-boot-dot" />
          <span className="evreg-boot-dot" />
          <span className="evreg-boot-dot" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container py-5 text-center">
        <h3>{t("reg.event_not_found", "Événement introuvable")}</h3>
        <Link to="/calendrier" className="btn btn-primary mt-3">{t("menu.calendrier", "Calendrier")}</Link>
      </div>
    );
  }

  // --- Lifecycle -------------------------------------------------------
  // The API sends `phase` once API_event_closed_GUARD.php is applied. Until
  // then we derive it from the dates, so a finished event is read-only either
  // way. Order matters: finished wins over every other state.
  const now = Date.now();
  const endTs   = event.date_fin ? new Date(event.date_fin).getTime()
                : event.date_debut ? new Date(event.date_debut).getTime() : null;
  const startTs = event.date_debut ? new Date(event.date_debut).getTime() : null;
  const limitTs = event.date_limite_inscription
                ? new Date(event.date_limite_inscription).getTime() : null;

  const phase =
      event.phase ? event.phase
    : (endTs && endTs < now) ? "finished"
    : (startTs && startTs <= now && endTs && endTs >= now) ? "running"
    : (limitTs && limitTs < now) ? "closing"
    : !event.registration_open ? "suspended"
    : "upcoming";

  const isPast  = phase === "finished" || phase === "running";
  const full    = event.places_restantes === 0;
  const closed  = phase !== "upcoming";
  // Organizer can disable e-mail OTP verification per event.
  const requireOtp = event.require_otp !== false;

  const devise = event.devise || "DZD";
  const poster = event.image ? `${BASE_URL}${event.image}` : null;
  const hasPoster = !!poster && posterOk;

  const fmtDate = (d, withTime) =>
    new Date(d).toLocaleDateString(lang, {
      year: "numeric", month: "long", day: "numeric",
      ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });

  // Days remaining before the event starts (null when it already started).
  let daysLeft = null;
  if (event.date_debut) {
    const diff = Math.ceil((new Date(event.date_debut) - new Date()) / 86400000);
    if (diff >= 0) daysLeft = diff;
  }

  // Seat occupancy — only meaningful when a capacity was set.
  const cap = Number(event.capacite_max) || 0;
  const left = event.places_restantes;
  const hasSeatBar = cap > 0 && left !== null && left !== undefined;
  const takenPct = hasSeatBar ? Math.min(100, Math.max(0, Math.round(((cap - left) / cap) * 100))) : 0;

  return (
    <div className="evreg">
      <ToastContainer position="top-center" autoClose={2500} rtl={lang === "ar"} pauseOnHover />
      <style>{regCss}</style>

      {/* ───────────────────────── HERO ───────────────────────── */}
      <header className={`evreg-hero ${hasPoster ? "" : "is-plain"} ${isPast ? "is-over" : ""}`}>
        {hasPoster && (
          <div className="evreg-ambient" style={{ backgroundImage: `url(${poster})` }} aria-hidden="true" />
        )}
        <div className="evreg-veil" aria-hidden="true" />

        <div className="container evreg-hero-inner">
          <div className={`evreg-hero-grid ${hasPoster ? "" : "is-textonly"}`}>

            {hasPoster && (
              <figure className="evreg-poster">
                <img
                  src={poster}
                  alt={event.title}
                  loading="eager"
                  onError={() => setPosterOk(false)}
                />
              </figure>
            )}

            <div className="evreg-hero-text">
              <p className="evreg-eyebrow">
                <span className="evreg-eyebrow-rule" aria-hidden="true" />
                {event.category || t("reg.eyebrow", "Inscription en ligne")}
              </p>

              <h1 className="evreg-title">{event.title}</h1>

              <ul className="evreg-facts">
                {event.date_debut && (
                  <li>
                    <i className="bi bi-calendar-event" aria-hidden="true" />
                    <span>
                      {fmtDate(event.date_debut, true)}
                      {event.date_fin && new Date(event.date_fin).toDateString() !== new Date(event.date_debut).toDateString()
                        ? <> — {fmtDate(event.date_fin, false)}</>
                        : null}
                    </span>
                  </li>
                )}
                {event.lieu && (
                  <li><i className="bi bi-geo-alt" aria-hidden="true" /><span>{event.lieu}</span></li>
                )}
                {daysLeft !== null && !isPast && (
                  <li className="evreg-fact-countdown">
                    <i className="bi bi-hourglass-split" aria-hidden="true" />
                    <span>
                      {daysLeft === 0
                        ? t("reg.today", "C'est aujourd'hui")
                        : `${t("reg.in", "Dans")} ${daysLeft} ${daysLeft > 1 ? t("reg.days", "jours") : t("reg.day", "jour")}`}
                    </span>
                  </li>
                )}
              </ul>

              <div className="evreg-chips">
                {isPast && (
                  <span className="evreg-chip is-over">
                    <i className={`bi ${phase === "running" ? "bi-broadcast" : "bi-archive"}`} aria-hidden="true" />
                    {phase === "running"
                      ? t("reg.running_badge", "En cours")
                      : t("reg.finished_badge", "Terminé")}
                  </span>
                )}
                {event.is_paid ? (
                  <span className="evreg-chip is-price">
                    <i className="bi bi-cash-coin" aria-hidden="true" />
                    {Number(event.prix).toLocaleString(lang)} {devise}
                    <small>{t("reg.per_person", "/ participant")}</small>
                  </span>
                ) : (
                  <span className="evreg-chip is-free">
                    <i className="bi bi-gift" aria-hidden="true" />
                    {t("reg.free", "Gratuit")}
                  </span>
                )}

                {left !== null && left !== undefined && (
                  <span className={`evreg-chip is-seats ${left <= 10 ? "is-low" : ""}`}>
                    <i className="bi bi-people" aria-hidden="true" />
                    {left} {t("reg.seats_left", "places restantes")}
                  </span>
                )}

                {event.date_limite_inscription && (
                  <span className="evreg-chip is-deadline">
                    <i className="bi bi-clock-history" aria-hidden="true" />
                    {t("reg.deadline", "Clôture")} {fmtDate(event.date_limite_inscription, false)}
                  </span>
                )}
              </div>

              {hasSeatBar && (
                <div className="evreg-gauge" role="img"
                     aria-label={`${cap - left}/${cap} ${t("reg.seats_taken", "places réservées")}`}>
                  <div className="evreg-gauge-track">
                    <span className="evreg-gauge-fill" style={{ "--pct": `${takenPct}%` }} />
                  </div>
                  <p className="evreg-gauge-label">
                    {cap - left} / {cap} {t("reg.seats_taken", "places réservées")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ───────────────────────── BODY ───────────────────────── */}
      <section className="evreg-body">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">

              {event.description && (
                <div className={`evreg-brief ${briefOpen ? "is-open" : ""}`}>
                  <div className="evreg-brief-body" dangerouslySetInnerHTML={{ __html: event.description }} />
                  <button type="button" className="evreg-brief-toggle"
                          aria-expanded={briefOpen}
                          onClick={() => setBriefOpen((o) => !o)}>
                    {briefOpen ? t("reg.read_less", "Réduire") : t("reg.read_more", "Lire la présentation")}
                    <i className="bi bi-chevron-down" aria-hidden="true" />
                  </button>
                </div>
              )}

              {event.registration_instructions && (
                <div className="evreg-note">
                  <i className="bi bi-info-circle" aria-hidden="true" />
                  <span dangerouslySetInnerHTML={{ __html: event.registration_instructions }} />
                </div>
              )}

              {/* Closed / full guards */}
              {closed ? (
                <div className={`evreg-over ${isPast ? "is-past" : ""}`}>
                  <span className="evreg-over-ico">
                    <i className={`bi ${phase === "finished" ? "bi-archive"
                                      : phase === "running"  ? "bi-broadcast"
                                      : phase === "closing"  ? "bi-hourglass-bottom"
                                      : "bi-lock"}`} aria-hidden="true" />
                  </span>
                  <h2 className="evreg-over-title">
                    {phase === "finished" && t("reg.finished_title", "Événement terminé")}
                    {phase === "running"  && t("reg.running_title", "Événement en cours")}
                    {phase === "closing"  && t("reg.closing_title", "Inscriptions closes")}
                    {phase === "suspended"&& t("reg.suspended_title", "Inscriptions non ouvertes")}
                  </h2>
                  <p className="evreg-over-text">
                    {event.closed_reason || (
                      phase === "finished"
                        ? t("reg.finished_text", "Cet événement a eu lieu. Vous pouvez consulter ses informations ci-dessus, mais il n'est plus possible de s'y inscrire.")
                      : phase === "running"
                        ? t("reg.running_text", "Cet événement se déroule en ce moment. Les inscriptions en ligne sont closes.")
                      : phase === "closing"
                        ? t("reg.closing_text", "La date limite d'inscription est dépassée.")
                        : t("reg.closed", "Les inscriptions pour cet événement ne sont pas ouvertes.")
                    )}
                  </p>
                  <div className="evreg-over-actions">
                    <Link to="/calendrier" className="evreg-over-btn is-primary">
                      <i className="bi bi-calendar3" aria-hidden="true" />
                      {t("reg.see_calendar", "Voir les prochains événements")}
                    </Link>
                    <Link to="/evenements" className="evreg-over-btn">
                      <i className="bi bi-collection" aria-hidden="true" />
                      {t("reg.all_events", "Tous les événements")}
                    </Link>
                  </div>
                </div>
              ) : full ? (
                <div className="evreg-guard is-full">
                  <i className="bi bi-x-octagon" aria-hidden="true" />
                  <span>{t("reg.full", "Cet événement est complet.")}</span>
                </div>
              ) : (event.registration_mode === "both" && !regMode) ? (
                /* ── Mode chooser (events configured as "both") ── */
                <div className="evreg-choose">
                  <h2 className="evreg-choose-title">{t("reg.choose_mode", "Comment souhaitez-vous vous inscrire ?")}</h2>
                  <div className="evreg-choose-grid">
                    <button type="button" className="evreg-choice" onClick={() => { setRegMode("company"); setStep(1); }}>
                      <span className="evreg-choice-ico"><i className="bi bi-building" aria-hidden="true" /></span>
                      <span className="evreg-choice-title">{t("reg.mode_company", "Au nom d'une entreprise")}</span>
                      <span className="evreg-choice-desc">{t("reg.mode_company_desc", "Inscrire une entreprise et un ou plusieurs participants.")}</span>
                      <span className="evreg-choice-go"><i className="bi bi-arrow-right" aria-hidden="true" /></span>
                    </button>
                    <button type="button" className="evreg-choice" onClick={() => { setRegMode("direct"); setStep(2); }}>
                      <span className="evreg-choice-ico"><i className="bi bi-person" aria-hidden="true" /></span>
                      <span className="evreg-choice-title">{t("reg.mode_direct", "À titre individuel")}</span>
                      <span className="evreg-choice-desc">{t("reg.mode_direct_desc", "S'inscrire seul, sans entreprise.")}</span>
                      <span className="evreg-choice-go"><i className="bi bi-arrow-right" aria-hidden="true" /></span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Progress rail — the two steps are a real sequence */}
                  {regMode === "company" && (
                    <ol className="evreg-rail" aria-label={t("reg.progress", "Progression")}>
                      <span className="evreg-rail-track" aria-hidden="true">
                        <span className="evreg-rail-fill" style={{ "--rail": step > 1 ? "100%" : "0%" }} />
                      </span>
                      <li className={`evreg-rail-step ${step === 1 ? "is-active" : "is-done"}`}>
                        <span className="evreg-rail-dot">
                          {step > 1 ? <i className="bi bi-check-lg" aria-hidden="true" /> : "1"}
                        </span>
                        <span className="evreg-rail-label">{t("reg.company_step", "Entreprise")}</span>
                      </li>
                      <li className={`evreg-rail-step ${step === 2 ? "is-active" : ""}`}>
                        <span className="evreg-rail-dot">2</span>
                        <span className="evreg-rail-label">{t("reg.participants_step", "Participants")}</span>
                      </li>
                    </ol>
                  )}

                  {/* If "both", allow going back to the chooser */}
                  {event.registration_mode === "both" && (
                    <div className="evreg-backline">
                      <button type="button" className="evreg-back" onClick={() => { setRegMode(null); setStep(1); }}>
                        <i className="bi bi-arrow-left" aria-hidden="true" /> {t("reg.change_mode", "Changer de mode")}
                      </button>
                    </div>
                  )}

                  {/* key → the entrance animation replays on every step change */}
                  <div className="evreg-stage" ref={stageRef} key={`${regMode}-${step}`}>
                    {regMode === "company" && step === 1 && (
                      <RegCompanyStep
                        idEvent={event.id}
                        company={company}
                        setCompany={setCompany}
                        otpVerified={otpVerified}
                        setOtpVerified={setOtpVerified}
                        requireOtp={requireOtp}
                        onNext={() => setStep(2)}
                        onExistingRegistration={handleExistingRegistration}
                      />
                    )}

                    {/* Existing-registration banner (company already registered) */}
                    {regMode === "company" && step === 2 && existingReg && (
                      <div className="evreg-existing">
                        <div>
                          <i className="bi bi-clipboard-check" aria-hidden="true" />
                          <strong>{t("reg.already_registered", "Cette entreprise est déjà inscrite à cet événement.")}</strong>
                          <span className="evreg-existing-ref">{t("reg.reference", "Référence")}: {existingReg.reference} · {t("reg.status", "Statut")}: {existingReg.statut}</span>
                          <div className="evreg-existing-hint">{t("reg.manage_hint", "Vous pouvez ajouter ou retirer des participants, ou annuler l'inscription.")}</div>
                        </div>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleCancelExisting} disabled={submitting}>
                          <i className="bi bi-x-circle me-1" aria-hidden="true" />{t("reg.cancel_registration", "Annuler l'inscription")}
                        </button>
                      </div>
                    )}

                    {((regMode === "company" && step === 2) || regMode === "direct") && (
                      <RegParticipantsStep
                        participants={participants}
                        setParticipants={setParticipants}
                        onBack={regMode === "company" ? () => setStep(1) : null}
                        onSubmit={existingReg ? handleUpdateExisting : handleSubmit}
                        submitting={submitting}
                        isPaid={event.is_paid}
                        prix={event.prix}
                        tauxTva={event.taux_tva}
                        paymentMethods={event.payment_methods}
                        payment={payment}
                        setPayment={setPayment}
                        idEvent={event.id}
                        mode={regMode}
                        requireOtp={requireOtp}
                        isUpdate={!!existingReg}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Full-screen veil while the registration is being sent */}
      {submitting && (
        <div className="evreg-sending" role="status" aria-live="polite">
          <div className="evreg-sending-card">
            <span className="evreg-spinner" aria-hidden="true" />
            <p>{t("reg.sending", "Enregistrement de votre inscription…")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const regCss = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=Instrument+Sans:wght@400;500;600;700&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&display=swap');

  .evreg {
    /* Comptoir palette. These are the same values CaciUI defines; the
       registration flow keeps its own --rg-* namespace so it stays
       self-contained, but the colours are now identical to every other page.
       RegCompanyStep and RegParticipantsStep carry no styles of their own,
       so retuning here moves all three files at once. */
    --rg-ink:#08150E;
    --rg-forest:#0F4A30;
    --rg-green:#1A7A4A;
    --rg-leaf:#22A060;
    --rg-brass:#B08A2E;
    --rg-brass-l:#D8B455;
    --rg-paper:#FAFBFA;
    --rg-line:#E4EDE8;
    --rg-muted:#5C7268;
    --rg-shadow:0 18px 50px -22px rgba(8,21,14,.45);
    --rg-font:'Instrument Sans','IBM Plex Sans Arabic',system-ui,sans-serif;
    --rg-ar:'IBM Plex Sans Arabic','Instrument Sans',system-ui,sans-serif;
    --rg-display:'Bricolage Grotesque','IBM Plex Sans Arabic',system-ui,sans-serif;
    --rg-ease:cubic-bezier(.22,.68,.36,1);
    font-family:var(--rg-font);
  }
  [dir="rtl"] .evreg { font-family:var(--rg-ar); letter-spacing:0; }
  /* Display face on headings, as on the rest of the site. */
  .evreg h1, .evreg h2, .evreg h3, .evreg .evreg-title {
    font-family:var(--rg-display); letter-spacing:-.028em;
  }
  [dir="rtl"] .evreg h1, [dir="rtl"] .evreg h2,
  [dir="rtl"] .evreg h3, [dir="rtl"] .evreg .evreg-title {
    font-family:var(--rg-ar); letter-spacing:0;
  }
  [dir="rtl"] .evreg .evreg-brief-body,
  [dir="rtl"] .evreg .evreg-note { line-height:2; }

  /* ── motion vocabulary ───────────────────────────────────── */
  @keyframes rgVeil   { from{opacity:0} to{opacity:1} }
  @keyframes rgRise   { from{opacity:0; transform:translateY(22px)} to{opacity:1; transform:none} }
  @keyframes rgPoster { from{opacity:0; transform:translateY(30px) scale(.94) rotate(-1.2deg)}
                        to  {opacity:1; transform:none} }
  @keyframes rgGauge  { from{width:0} to{width:var(--pct,0%)} }
  @keyframes rgRail   { from{width:0} to{width:var(--rail,0%)} }
  @keyframes rgPulse  { 0%{box-shadow:0 0 0 0 rgba(47,157,99,.45)}
                        70%{box-shadow:0 0 0 12px rgba(47,157,99,0)}
                        100%{box-shadow:0 0 0 0 rgba(47,157,99,0)} }
  @keyframes rgSheen  { from{transform:translateX(-120%)} to{transform:translateX(220%)} }
  @keyframes rgSpin   { to{transform:rotate(360deg)} }
  @keyframes rgBounce { 0%,80%,100%{transform:scale(.6);opacity:.45}
                        40%{transform:scale(1);opacity:1} }

  /* ── boot placeholder ────────────────────────────────────── */
  .evreg-boot { display:flex; gap:10px; justify-content:center; padding:110px 0; }
  .evreg-boot-dot { width:12px; height:12px; border-radius:50%; background:var(--rg-leaf);
                    animation:rgBounce 1.3s var(--rg-ease) infinite; }
  .evreg-boot-dot:nth-child(2){ animation-delay:.16s }
  .evreg-boot-dot:nth-child(3){ animation-delay:.32s }

  /* ── HERO ────────────────────────────────────────────────── */
  /* a finished event is presented as an archive, not as an offer */
  .evreg-hero.is-over .evreg-poster img { filter:saturate(.55); }
  .evreg-hero.is-over .evreg-ambient { filter:blur(38px) saturate(.5) brightness(.42); }
  .evreg-chip.is-over { background:rgba(255,255,255,.16); border-color:rgba(255,255,255,.3); }

  .evreg-over { text-align:center; background:#fff; border:1px solid var(--rg-line);
                border-radius:20px; padding:46px 32px 40px;
                box-shadow:0 12px 44px -30px rgba(15,35,24,.5);
                animation:rgRise .6s var(--rg-ease) both; }
  .evreg-over-ico { display:inline-grid; place-items:center; width:66px; height:66px;
                    border-radius:20px; margin-bottom:20px; font-size:27px;
                    background:#eef4f0; color:var(--rg-muted); }
  .evreg-over.is-past .evreg-over-ico { background:#f0ece3; color:var(--rg-brass); }
  .evreg-over-title { font-size:22px; font-weight:700; color:var(--rg-ink); margin:0 0 10px; }
  .evreg-over-text { font-size:15px; line-height:1.8; color:var(--rg-muted);
                     max-width:520px; margin:0 auto 26px; }
  .evreg-over-actions { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; }
  .evreg-over-btn { display:inline-flex; align-items:center; gap:9px; padding:12px 24px;
                    border-radius:12px; font-size:14px; font-weight:600; text-decoration:none;
                    border:1px solid var(--rg-line); color:var(--rg-ink); background:#fff;
                    transition:all .25s var(--rg-ease); }
  .evreg-over-btn:hover { transform:translateY(-2px); border-color:var(--rg-leaf); color:var(--rg-forest); }
  .evreg-over-btn.is-primary { background:linear-gradient(135deg,var(--rg-forest),var(--rg-leaf));
                    color:#fff; border-color:transparent;
                    box-shadow:0 10px 24px -12px rgba(21,86,50,.85); }
  .evreg-over-btn.is-primary:hover { color:#fff; filter:brightness(1.06); }

  .evreg-hero { position:relative; isolation:isolate; overflow:hidden;
                background:linear-gradient(150deg,#0f2318 0%,#155632 58%,#1c7041 100%); }
  .evreg-ambient {
    position:absolute; inset:-8%; z-index:0;
    background-size:cover; background-position:center;
    filter:blur(38px) saturate(1.35) brightness(.62);
    transform:scale(1.18);
    animation:rgVeil 1s var(--rg-ease) both;
  }
  .evreg-veil {
    position:absolute; inset:0; z-index:1;
    background:
      radial-gradient(120% 90% at 15% 0%, rgba(47,157,99,.30) 0%, transparent 60%),
      linear-gradient(to bottom, rgba(10,26,18,.62) 0%, rgba(10,26,18,.80) 100%);
  }
  .evreg-hero-inner { position:relative; z-index:2; }
  .evreg-hero-grid {
    display:grid; grid-template-columns:minmax(0,420px) minmax(0,1fr);
    gap:44px; align-items:center; padding:56px 0 60px;
  }
  .evreg-hero-grid.is-textonly { grid-template-columns:minmax(0,1fr); }

  /* the poster is an artifact: shown whole, never cropped */
  .evreg-poster { margin:0; animation:rgPoster .9s var(--rg-ease) both; animation-delay:.1s; }
  .evreg-poster img {
    display:block; width:100%; height:auto; max-height:400px;
    object-fit:contain; border-radius:16px;
    background:rgba(255,255,255,.05);
    box-shadow:0 26px 60px -20px rgba(0,0,0,.72), 0 0 0 1px rgba(255,255,255,.14);
    transition:transform .55s var(--rg-ease), box-shadow .55s var(--rg-ease);
  }
  .evreg-poster:hover img { transform:translateY(-6px) scale(1.012);
                            box-shadow:0 34px 70px -18px rgba(0,0,0,.78), 0 0 0 1px rgba(255,255,255,.22); }

  .evreg-hero-text { color:#fff; min-width:0; }
  .evreg-eyebrow {
    display:flex; align-items:center; gap:12px;
    font-size:11.5px; font-weight:700; letter-spacing:.22em; text-transform:uppercase;
    color:var(--rg-brass-l); margin:0 0 14px;
    animation:rgRise .7s var(--rg-ease) both; animation-delay:.22s;
  }
  .evreg-eyebrow-rule { width:34px; height:2px; background:var(--rg-brass); border-radius:2px; flex:none; }
  [dir="rtl"] .evreg-eyebrow { letter-spacing:0; }

  .evreg-title {
    font-size:clamp(30px,4.4vw,52px); font-weight:800; line-height:1.08;
    letter-spacing:-.02em; margin:0 0 18px; color:#fff;
    text-shadow:0 2px 24px rgba(0,0,0,.45);
    animation:rgRise .7s var(--rg-ease) both; animation-delay:.3s;
  }
  [dir="rtl"] .evreg-title { letter-spacing:0; line-height:1.35; }

  .evreg-facts { list-style:none; margin:0 0 20px; padding:0; display:flex; flex-wrap:wrap; gap:8px 26px;
                 animation:rgRise .7s var(--rg-ease) both; animation-delay:.38s; }
  .evreg-facts li { display:flex; align-items:center; gap:9px; font-size:14.5px; color:rgba(255,255,255,.92); }
  .evreg-facts i { color:var(--rg-brass-l); font-size:15px; }
  .evreg-fact-countdown { font-weight:600; }

  .evreg-chips { display:flex; flex-wrap:wrap; gap:10px; margin-bottom:20px;
                 animation:rgRise .7s var(--rg-ease) both; animation-delay:.46s; }
  .evreg-chip {
    position:relative; overflow:hidden; display:inline-flex; align-items:center; gap:8px;
    padding:9px 18px; border-radius:999px; font-size:13.5px; font-weight:700; color:#fff;
    border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1);
    backdrop-filter:blur(6px);
  }
  .evreg-chip small { font-weight:500; opacity:.8; font-size:11.5px; }
  .evreg-chip.is-price { background:linear-gradient(120deg,var(--rg-brass) 0%,var(--rg-brass-l) 100%);
                         border-color:transparent; color:#241704; }
  .evreg-chip.is-price small { opacity:.72; }
  /* a single sheen sweep across the price — the one flourish on the page */
  .evreg-chip.is-price::after {
    content:""; position:absolute; top:0; bottom:0; width:38%;
    background:linear-gradient(100deg,transparent,rgba(255,255,255,.55),transparent);
    animation:rgSheen 2.6s var(--rg-ease) 1.2s 2;
  }
  .evreg-chip.is-free  { background:rgba(47,157,99,.92); border-color:transparent; }
  .evreg-chip.is-seats.is-low { background:rgba(214,90,50,.92); border-color:transparent; }

  .evreg-gauge { max-width:420px; animation:rgRise .7s var(--rg-ease) both; animation-delay:.54s; }
  .evreg-gauge-track { height:6px; border-radius:99px; background:rgba(255,255,255,.18); overflow:hidden; }
  .evreg-gauge-fill { display:block; height:100%; border-radius:99px;
                      background:linear-gradient(90deg,var(--rg-leaf),var(--rg-brass-l));
                      animation:rgGauge 1.1s var(--rg-ease) .7s both; }
  .evreg-gauge-label { margin:8px 0 0; font-size:12.5px; color:rgba(255,255,255,.72); }

  /* ── BODY ────────────────────────────────────────────────── */
  .evreg-body { background:var(--rg-paper); padding:42px 0 72px; }

  /* collapsible brief keeps the form within reach */
  .evreg-brief { position:relative; background:#fff; border:1px solid var(--rg-line);
                 border-radius:16px; padding:26px 28px 8px; margin-bottom:18px;
                 box-shadow:0 2px 14px -8px rgba(15,35,24,.3);
                 animation:rgRise .6s var(--rg-ease) both; animation-delay:.1s; }
  .evreg-brief-body { position:relative; max-height:190px; overflow:hidden;
                      color:#3d4f45; font-size:15px; line-height:1.85;
                      transition:max-height .5s var(--rg-ease); }
  .evreg-brief.is-open .evreg-brief-body { max-height:2600px; }
  .evreg-brief-body::after { content:""; position:absolute; left:0; right:0; bottom:0; height:70px;
                             background:linear-gradient(to bottom,rgba(255,255,255,0),#fff);
                             transition:opacity .35s var(--rg-ease); }
  .evreg-brief.is-open .evreg-brief-body::after { opacity:0; pointer-events:none; }
  .evreg-brief-body a { color:var(--rg-green); font-weight:600; }
  .evreg-brief-toggle { display:inline-flex; align-items:center; gap:7px; margin:14px 0 18px;
                        background:none; border:0; padding:0; cursor:pointer;
                        color:var(--rg-green); font-weight:700; font-size:13.5px; font-family:inherit; }
  .evreg-brief-toggle i { transition:transform .35s var(--rg-ease); }
  .evreg-brief.is-open .evreg-brief-toggle i { transform:rotate(180deg); }
  .evreg-brief-toggle:hover { color:var(--rg-forest); }

  .evreg-note { display:flex; gap:12px; align-items:flex-start;
                background:#eaf5ee; border:1px solid #cfe6d9; border-left:4px solid var(--rg-green);
                border-radius:12px; padding:15px 18px; margin-bottom:22px;
                color:#264834; font-size:14.5px; line-height:1.7;
                animation:rgRise .6s var(--rg-ease) both; animation-delay:.16s; }
  .evreg-note i { color:var(--rg-green); font-size:17px; margin-top:2px; flex:none; }
  [dir="rtl"] .evreg-note { border-left:1px solid #cfe6d9; border-right:4px solid var(--rg-green); }

  .evreg-guard { display:flex; gap:12px; align-items:center; justify-content:center;
                 border-radius:16px; padding:30px 24px; font-size:16px; font-weight:600;
                 animation:rgRise .6s var(--rg-ease) both; }
  .evreg-guard i { font-size:21px; }
  .evreg-guard.is-closed { background:#fff4e2; border:1px solid #f5d9a8; color:#8a5a13; }
  .evreg-guard.is-full   { background:#fdeceb; border:1px solid #f5c6c2; color:#992a1f; }

  /* ── mode chooser ────────────────────────────────────────── */
  .evreg-choose { animation:rgRise .6s var(--rg-ease) both; animation-delay:.2s; }
  .evreg-choose-title { font-size:20px; font-weight:700; color:var(--rg-ink); text-align:center; margin:6px 0 22px; }
  .evreg-choose-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(250px,1fr)); gap:18px; }
  .evreg-choice { position:relative; display:flex; flex-direction:column; align-items:flex-start; gap:8px;
                  text-align:left; background:#fff; border:1px solid var(--rg-line); border-radius:18px;
                  padding:26px 24px 24px; cursor:pointer; font-family:inherit;
                  transition:transform .3s var(--rg-ease), box-shadow .3s var(--rg-ease), border-color .3s var(--rg-ease); }
  [dir="rtl"] .evreg-choice { text-align:right; align-items:flex-end; }
  .evreg-choice:hover { transform:translateY(-5px); border-color:var(--rg-leaf); box-shadow:var(--rg-shadow); }
  .evreg-choice-ico { width:50px; height:50px; border-radius:14px; display:grid; place-items:center;
                      background:linear-gradient(135deg,var(--rg-forest),var(--rg-leaf)); color:#fff;
                      font-size:22px; margin-bottom:6px;
                      transition:transform .35s var(--rg-ease); }
  .evreg-choice:hover .evreg-choice-ico { transform:scale(1.08) rotate(-4deg); }
  .evreg-choice-title { font-size:16px; font-weight:700; color:var(--rg-ink); }
  .evreg-choice-desc { font-size:13.5px; color:var(--rg-muted); line-height:1.65; }
  .evreg-choice-go { position:absolute; top:24px; right:24px; color:var(--rg-leaf); opacity:0;
                     transform:translateX(-6px); transition:all .3s var(--rg-ease); }
  [dir="rtl"] .evreg-choice-go { right:auto; left:24px; transform:scaleX(-1) translateX(-6px); }
  .evreg-choice:hover .evreg-choice-go { opacity:1; transform:translateX(0); }
  [dir="rtl"] .evreg-choice:hover .evreg-choice-go { transform:scaleX(-1) translateX(0); }

  /* ── progress rail ───────────────────────────────────────── */
  .evreg-rail { position:relative; list-style:none; margin:0 auto 24px; padding:0;
                display:flex; justify-content:space-between; max-width:440px;
                animation:rgRise .6s var(--rg-ease) both; animation-delay:.22s; }
  .evreg-rail-track { position:absolute; top:19px; left:19px; right:19px; height:3px;
                      background:var(--rg-line); border-radius:99px; overflow:hidden; }
  .evreg-rail-fill { display:block; height:100%; border-radius:99px;
                     background:linear-gradient(90deg,var(--rg-green),var(--rg-leaf));
                     width:var(--rail,0%); transition:width .6s var(--rg-ease); }
  .evreg-rail-step { position:relative; display:flex; flex-direction:column; align-items:center; gap:8px; }
  .evreg-rail-dot { width:38px; height:38px; border-radius:50%; display:grid; place-items:center;
                    background:#fff; border:2px solid var(--rg-line); color:var(--rg-muted);
                    font-weight:700; font-size:14px;
                    transition:all .4s var(--rg-ease); }
  .evreg-rail-step.is-active .evreg-rail-dot { background:linear-gradient(135deg,var(--rg-forest),var(--rg-leaf));
                    border-color:transparent; color:#fff; animation:rgPulse 2.2s var(--rg-ease) infinite; }
  .evreg-rail-step.is-done .evreg-rail-dot { background:var(--rg-leaf); border-color:transparent; color:#fff; }
  .evreg-rail-label { font-size:12.5px; font-weight:600; color:var(--rg-muted); }
  .evreg-rail-step.is-active .evreg-rail-label { color:var(--rg-forest); }

  .evreg-backline { text-align:center; margin-bottom:14px; }
  .evreg-back { background:none; border:0; color:var(--rg-muted); font-size:13.5px; font-weight:600;
                cursor:pointer; font-family:inherit; padding:6px 10px; border-radius:8px;
                transition:color .25s var(--rg-ease), background .25s var(--rg-ease); }
  .evreg-back:hover { color:var(--rg-forest); background:#e9f3ed; }

  /* ── the form stage (children come from RegCompanyStep / RegParticipantsStep) ── */
  .evreg-stage { animation:rgRise .55s var(--rg-ease) both; scroll-margin-top:90px; }
  .evreg-stage .card { border:1px solid var(--rg-line) !important; border-radius:18px !important;
                       box-shadow:0 10px 40px -26px rgba(15,35,24,.5) !important; overflow:hidden; }
  .evreg-stage .card-header { background:#fff !important; border-bottom:1px solid var(--rg-line) !important;
                              padding:20px 26px !important; font-weight:700; color:var(--rg-ink); }
  .evreg-stage .card-body { padding:26px !important; }
  .evreg-stage .form-label { font-size:13px; font-weight:600; color:#33463b; margin-bottom:6px; }
  .evreg-stage .form-control, .evreg-stage .form-select {
    border:1px solid #dbe7e0; border-radius:11px; padding:11px 14px; font-size:14.5px;
    background:#fcfefd; color:var(--rg-ink);
    transition:border-color .25s var(--rg-ease), box-shadow .25s var(--rg-ease), background .25s var(--rg-ease);
  }
  .evreg-stage .form-control:focus, .evreg-stage .form-select:focus {
    border-color:var(--rg-leaf); background:#fff; outline:none;
    box-shadow:0 0 0 4px rgba(47,157,99,.14);
  }
  .evreg-stage .form-control::placeholder { color:#a9b9b0; }
  .evreg-stage .btn { border-radius:11px; font-weight:600; padding:11px 22px; font-size:14.5px;
                      transition:transform .22s var(--rg-ease), box-shadow .22s var(--rg-ease), filter .22s var(--rg-ease); }
  .evreg-stage .btn:hover:not(:disabled) { transform:translateY(-2px); }
  .evreg-stage .btn-primary, .evreg-stage .btn-success {
    background:linear-gradient(135deg,var(--rg-forest),var(--rg-leaf)); border:0;
    box-shadow:0 10px 24px -12px rgba(21,86,50,.85);
  }
  .evreg-stage .btn-primary:hover:not(:disabled), .evreg-stage .btn-success:hover:not(:disabled) {
    filter:brightness(1.06); box-shadow:0 16px 30px -12px rgba(21,86,50,.9);
  }
  .evreg-stage .btn:disabled { opacity:.55; transform:none; }
  /* stagger the participant blocks as they appear */
  .evreg-stage .card-body > .border { animation:rgRise .45s var(--rg-ease) both; }
  .evreg-stage .card-body > .border:nth-child(2){ animation-delay:.05s }
  .evreg-stage .card-body > .border:nth-child(3){ animation-delay:.1s }
  .evreg-stage .card-body > .border:nth-child(4){ animation-delay:.15s }

  /* ── existing registration ───────────────────────────────── */
  .evreg-existing { display:flex; justify-content:space-between; align-items:center; gap:16px; flex-wrap:wrap;
                    background:#fff8e6; border:1px solid #ffe3a3; border-left:4px solid var(--rg-brass);
                    border-radius:14px; padding:16px 18px; margin-bottom:18px;
                    animation:rgRise .5s var(--rg-ease) both; }
  [dir="rtl"] .evreg-existing { border-left:1px solid #ffe3a3; border-right:4px solid var(--rg-brass); }
  .evreg-existing i { color:#b26a00; margin-right:6px; }
  .evreg-existing strong { display:block; color:var(--rg-ink); font-size:15px; margin-bottom:2px; }
  .evreg-existing-ref { display:block; font-size:12.5px; color:#8a6d3b; font-family:ui-monospace,monospace; }
  .evreg-existing-hint { font-size:12.5px; color:#7a6a52; margin-top:4px; }

  /* ── sending veil ────────────────────────────────────────── */
  .evreg-sending { position:fixed; inset:0; z-index:2000; display:grid; place-items:center;
                   background:rgba(10,26,18,.55); backdrop-filter:blur(3px); animation:rgVeil .25s ease both; }
  .evreg-sending-card { background:#fff; border-radius:18px; padding:30px 38px; text-align:center;
                        box-shadow:var(--rg-shadow); }
  .evreg-sending-card p { margin:14px 0 0; font-size:14.5px; font-weight:600; color:var(--rg-ink); }
  .evreg-spinner { display:inline-block; width:38px; height:38px; border-radius:50%;
                   border:3px solid var(--rg-line); border-top-color:var(--rg-leaf);
                   animation:rgSpin .8s linear infinite; }

  /* ── responsive ──────────────────────────────────────────── */
  @media (max-width:991.98px) {
    .evreg-hero-grid { grid-template-columns:minmax(0,1fr); gap:28px; padding:38px 0 42px; }
    .evreg-poster img { max-height:300px; margin:0 auto; }
    .evreg-facts { gap:6px 18px; }
  }
  @media (max-width:575.98px) {
    .evreg-title { font-size:27px; }
    .evreg-brief { padding:20px 18px 4px; }
    .evreg-stage .card-body { padding:18px !important; }
    .evreg-rail-label { font-size:11.5px; }
    .evreg-chip { font-size:12.5px; padding:8px 14px; }
  }

  /* ── accessibility ───────────────────────────────────────── */
  .evreg :focus-visible { outline:3px solid var(--rg-leaf); outline-offset:2px; border-radius:6px; }
  @media (prefers-reduced-motion: reduce) {
    .evreg *, .evreg *::before, .evreg *::after {
      animation:none !important; transition:none !important;
    }
    .evreg-gauge-fill { width:var(--pct,0%) !important; }
    .evreg-rail-fill  { width:var(--rail,0%) !important; }
  }
`;

export default function EventRegister() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t("reg.title", "Inscription")} - {t("menu.caci", "CACI")}</title>
        <meta name="description" content={t("reg.meta", "Inscrivez votre entreprise et vos participants à cet événement.")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <div className="page-title dark-background">
        <div className="container position-relative">
          <h1>{t("reg.title", "Inscription")}</h1>
          <nav className="breadcrumbs">
            <ol>
              <li><Link to={i18n.language === "fr" ? "/" : `/${i18n.language}`}>{t("menu.home", "Accueil")}</Link></li>
              <li><Link to="/evenements">{t("events.title", "Événements")}</Link></li>
              <li className="current">{t("reg.title", "Inscription")}</li>
            </ol>
          </nav>
        </div>
      </div>

      <RegistrationCore slug={slug} layout="site" />
    </>
  );
}